<script setup lang="ts">
  import { formatNameWithInitial, formatNumber } from '@/utils/strings';
  import type { ChartData, ChartOptions } from 'chart.js';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
  import { Bar } from 'vue-chartjs';

  interface Creator {
    image: string | null;
    influencer_id: number;
    followers: number;
    name: string;
    date: string;
    impressions: number;
    media_type: string;
    reach: number;
    saved: number;
    shares: number;
    submission_link: string;
    total_interactions: number;
    platform: string;
    cost: number;
    engagement_rate: number;
    er: string;
    cpm: number;
    cpe: number;
    cpr: number;
    occurrences: number;
  }

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const props = defineProps<{
    creators?: Creator[];
  }>();

  const chartRef = ref<any>(null);

  const data = computed<ChartData<'bar'>>(() => {
    if (!props.creators || props.creators.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Sort creators by impressions in descending order
    const sortedCreators = [...props.creators].sort((a, b) => b.impressions - a.impressions);

    return {
      labels: sortedCreators.map(creator => creator.name),
      datasets: [
        {
          label: 'Impressions',
          data: sortedCreators.map(creator => creator.impressions),
          backgroundColor: '#0EA5E9',
          borderRadius: 8,
          barThickness: 46,
        },
      ],
    };
  });

  const options = computed<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        displayColors: false,
        backgroundColor: '#011627',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: function (tooltipItems) {
            return tooltipItems[0].label;
          },
          label: function (tooltipItem) {
            const value = tooltipItem.raw as number;
            return `Impressions: ${formatNumber(String(Math.round(value)))}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748B',
          font: {
            size: 12,
          },
          callback: function (val) {
            return formatNameWithInitial(String(this.getLabelForValue(val as number)));
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: '#E2E8F0',
          drawTicks: false,
        },
        ticks: {
          color: '#64748B',
          padding: 8,
          font: {
            size: 12,
          },
          callback: function (val) {
            return formatNumber(String(val), { notation: 'compact' });
          },
        },
        beginAtZero: true,
      },
    },
  }));

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    // Handle chart resize when container size changes
    const chartContainer = chartRef.value?.$el?.parentElement;
    if (chartContainer) {
      resizeObserver = new ResizeObserver(() => {
        if (chartRef.value?.chart) {
          chartRef.value.chart.resize();
        }
      });
      resizeObserver.observe(chartContainer);
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
</script>

<template>
  <div class="creator-performance-container flex flex-col gap-24">
    <h2 class="chart-title">Creator Performance</h2>
    <div class="chart-wrapper">
      <Bar v-if="props.creators" :data="data" :options="options" ref="chartRef" />
      <div v-else class="loading-state">Loading creator data...</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .creator-performance-container {
    background: white;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    height: 100%;

    .chart-title {
      font-size: 20px;
      font-weight: 600;
      color: #0f172a;
      flex-shrink: 0;

      @media screen and (min-width: 768px) {
        font-size: 24px;
      }
    }
  }

  .chart-wrapper {
    width: 100%;
    flex: 1;
    min-height: 300px;
    max-height: 500px;

    @media screen and (min-width: 768px) {
      min-height: 350px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 400px;
    }
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #64748b;
  }
</style>
